import * as React from "react";
//#region src/components/shared/use-horizontal-input-scroll-fade.ts
/**
* Horizontal overflow fades for text fields.
*
* Softens clipped values (and long placeholders) at the inline edges of
* single-line `input` elements — the same affordance DataTable uses on pinned
* column seams. Applied by setting `mask-image` on the field itself so text
* fades into the control background without overlay chrome.
*
* **Used by**
* - {@link TextInput} (`TextInputControl`) — default + flat variants
* - {@link AutocompleteInput} / {@link Combobox}
*
* **How sync works**
* Listeners cover scroll / input / change / resize / relevant mutations and
* always remasure layout. Controlled React updates often set the DOM `value`
* *property* without those events, so the hook also runs after every commit
* (reads the live DOM — no caller-supplied dependency list / prop drilling).
*
* Commit-path sync is cheap when nothing content-relevant changed: it compares
* a per-field fingerprint (`value`, `placeholder`, `scrollLeft`) and skips
* `scrollWidth` / placeholder measurement when the fingerprint is unchanged.
* Layout-affecting changes are expected to arrive via ResizeObserver /
* MutationObserver, which force a full remasure.
*
* **Composite fields** (e.g. booking address + suite): keep one shared outer
* border, strip chrome on nested triggers (`triggerClassName` / `variant="flat"`),
* and let each nested control own its own fade via this hook.
*
* Multiline `textarea`s are skipped — they scroll vertically.
*/
/** Max edge fade depth in px — matches DataTable pinned seam fade size. */
const FADE_SIZE_PX = 12;
/**
* Applies horizontal edge fades to overflowing single-line inputs inside
* `containerRef`.
*
* Callers pass only a container ref — no value/deps prop drilling. Remeasure
* is driven by DOM events plus a commit-time pass that bails early when the
* field's value / placeholder / scrollLeft have not changed.
*
* @param containerRef - Control / trigger that wraps the text field(s)
*/
function useHorizontalInputScrollFade(containerRef) {
	const fingerprintsRef = React.useRef(/* @__PURE__ */ new WeakMap());
	const sync = React.useCallback((options) => {
		const container = containerRef.current;
		if (!container) return;
		const forceLayout = options?.forceLayout ?? false;
		const fingerprints = fingerprintsRef.current;
		for (const field of container.querySelectorAll("input:not([type=\"hidden\"]), textarea")) {
			const nextFingerprint = readContentFingerprint(field);
			if (!forceLayout && shouldSkipLayoutRemeasure(fingerprints.get(field), nextFingerprint)) continue;
			const mask = buildFadeMask(field);
			fingerprints.set(field, nextFingerprint);
			if (field.style.maskImage === mask) continue;
			field.style.maskImage = mask;
			field.style.webkitMaskImage = mask;
		}
	}, [containerRef]);
	const syncFromEvent = React.useCallback(() => {
		sync({ forceLayout: true });
	}, [sync]);
	React.useLayoutEffect(() => {
		sync({ forceLayout: false });
	});
	React.useLayoutEffect(() => {
		const container = containerRef.current;
		if (!container) return;
		container.addEventListener("scroll", syncFromEvent, {
			capture: true,
			passive: true
		});
		container.addEventListener("input", syncFromEvent, true);
		container.addEventListener("change", syncFromEvent, true);
		const resizeObserver = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(syncFromEvent);
		if (resizeObserver) {
			resizeObserver.observe(container);
			for (const field of container.querySelectorAll("input, textarea")) resizeObserver.observe(field);
		}
		const mutationObserver = typeof MutationObserver === "undefined" ? null : new MutationObserver(syncFromEvent);
		mutationObserver?.observe(container, {
			attributes: true,
			attributeFilter: ["value", "placeholder"],
			characterData: true,
			childList: true,
			subtree: true
		});
		return () => {
			container.removeEventListener("scroll", syncFromEvent, true);
			container.removeEventListener("input", syncFromEvent, true);
			container.removeEventListener("change", syncFromEvent, true);
			resizeObserver?.disconnect();
			mutationObserver?.disconnect();
		};
	}, [containerRef, syncFromEvent]);
}
/**
* Reads the cheap content fingerprint used to skip commit-time layout work.
*
* @param field - Input or textarea to fingerprint
* @returns Value / placeholder / scrollLeft snapshot
*/
function readContentFingerprint(field) {
	return {
		placeholder: field.placeholder,
		scrollLeft: field.scrollLeft,
		value: field.value
	};
}
/**
* Whether commit-time sync can skip layout remasure for this field.
*
* Layout-affecting changes must arrive via forced sync (resize / mutation /
* scroll / input). Matching fingerprints mean a parent rerender with the same
* field content — keep the existing mask.
*
* @param previous - Last fingerprint stored for the field
* @param next - Current fingerprint from the DOM
* @returns True when layout remasure can be skipped
*/
function shouldSkipLayoutRemeasure(previous, next) {
	if (!previous) return false;
	return previous.value === next.value && previous.placeholder === next.placeholder && previous.scrollLeft === next.scrollLeft;
}
/**
* Returns a CSS `mask-image` for the field's current horizontal overflow, or
* an empty string when no fade is needed.
*
* @param field - Field to measure
* @returns CSS mask-image value, or `''` when no fade is required
*/
function buildFadeMask(field) {
	if (field instanceof HTMLTextAreaElement || field.getAttribute("type") === "hidden") return "";
	const overflow = Math.max(0, field.scrollWidth - field.clientWidth);
	const startPx = Math.min(FADE_SIZE_PX, field.scrollLeft);
	let endPx = Math.min(FADE_SIZE_PX, Math.max(0, overflow - field.scrollLeft));
	if (!field.value && field.placeholder && endPx === 0) {
		if (measureTextWidth(field, field.placeholder) > field.clientWidth) endPx = FADE_SIZE_PX;
	}
	if (startPx <= 0 && endPx <= 0) return "";
	return `linear-gradient(to right, transparent 0, #000 ${startPx}px, #000 calc(100% - ${endPx}px), transparent 100%)`;
}
/** Cache placeholder measurements keyed by font + text. */
const placeholderWidthCache = /* @__PURE__ */ new Map();
/**
* Measures text width with the field's computed font via a throwaway span.
*
* Avoids `canvas.getContext` (jsdom logs "Not implemented" and breaks suites
* that assert on a clean console). Results are cached by font + letter-spacing
* + text so empty-field placeholder checks stay cheap across syncs.
*
* @param element - Element whose computed font is used for measurement
* @param text - String to measure
* @returns Measured width in CSS pixels
*/
function measureTextWidth(element, text) {
	const style = window.getComputedStyle(element);
	const cacheKey = `${style.font}\0${style.letterSpacing}\0${text}`;
	const cached = placeholderWidthCache.get(cacheKey);
	if (cached !== void 0) return cached;
	const probe = document.createElement("span");
	probe.setAttribute("aria-hidden", "true");
	probe.style.cssText = [
		"position:absolute",
		"visibility:hidden",
		"white-space:nowrap",
		`font:${style.font}`,
		`letter-spacing:${style.letterSpacing}`
	].join(";");
	probe.textContent = text;
	document.body.appendChild(probe);
	const width = probe.getBoundingClientRect().width;
	probe.remove();
	placeholderWidthCache.set(cacheKey, width);
	return width;
}
/** @internal Exported for unit tests only. */
const __test = {
	FADE_SIZE_PX,
	buildFadeMask,
	shouldSkipLayoutRemeasure
};
//#endregion
export { __test, useHorizontalInputScrollFade };

//# sourceMappingURL=use-horizontal-input-scroll-fade.js.map